import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { isContractorWorkspaceEnabled } from './config/featureFlags';
import './utils/axiosConfig'; // Registers 401 handling for all axios/fetch API calls
import './index.css';
import App from './App/App';
import Login from './components/auth/Login/Login';
import Register from './components/auth/Register/Register';
import Dashboard from './components/pages/Dashboard/Dashboard';
import PropertyDashboard from './components/property/PropertyDashboard/PropertyDashboard';
import PropertyDashboardOverview from './components/property/PropertyDashboard/PropertyDashboardOverview';
import RoofSafety from './components/compliance/RoofSafety/RoofSafety';
import FireSafety from './components/compliance/FireSafety/FireSafety';
import StaffSafety from './components/compliance/StaffSafety/StaffSafety';
import EquipmentSafety from './components/compliance/EquipmentSafety/EquipmentSafety';
import ElectricalSafety from './components/compliance/ElectricalSafety/ElectricalSafety';
import BuildingMaintenance from './components/compliance/BuildingMaintenance/BuildingMaintenance';
import BuildingMaintenanceDashboard from './components/compliance/BuildingMaintenance/BuildingMaintenanceDashboard/BuildingMaintenanceDashboard';
import GeneralMaintenance from './components/compliance/BuildingMaintenance/GeneralMaintenance/GeneralMaintenance';
import ErrorPage from './components/pages/ErrorPage/ErrorPage';
import ComingSoon from './components/pages/ComingSoon/ComingSoon';
import 'react-toastify/dist/ReactToastify.css';
import ProtectedRoute from './components/auth/ProtectedRoute';
import QualificationsAdmin from './components/portals/Admin/QualificationsAdmin';
import MyQualifications from './components/portals/ContractorStaff/MyQualifications';
import DemoRedirect from './components/auth/DemoRedirect/DemoRedirect';
import PropertyLogs from './components/property/PropertyLogs/PropertyLogs';
import ActivityPage from './components/account/Activity/ActivityPage';
import Inspections from './components/compliance/Inspections/Inspections';
import Documents from './components/property/Documents/Documents';
import PinpointViewer from './components/property/PinpointViewer/PinpointViewer';
import OnboardingWizard from './components/property/OnboardingWizard/OnboardingWizard';
import EmergencyPreparedness from './components/compliance/EmergencyPreparedness/EmergencyPreparedness';
import EmergencyLighting from './components/compliance/EmergencyPreparedness/EmergencyLighting/EmergencyLighting';
import EscapeRoutes from './components/compliance/EmergencyPreparedness/EscapeRoutes/EscapeRoutes';
import AssemblyPoints from './components/compliance/EmergencyPreparedness/AssemblyPoints/AssemblyPoints';
import EmergencyPlans from './components/compliance/EmergencyPreparedness/EmergencyPlans/EmergencyPlans';
import Defibrillators from './components/compliance/EmergencyPreparedness/Defibrillators/Defibrillators';
import IncidentReporting from './components/compliance/EmergencyPreparedness/IncidentReporting/IncidentReporting';
import Settings from './components/account/Settings/Settings';
import SettingsDashboard from './components/account/Settings/SettingsDashboard/SettingsDashboard';
import CompanyInformation from './components/account/Settings/CompanyInformation/CompanyInformation';
import ApplicationSettings from './components/account/Settings/ApplicationSettings/ApplicationSettings';
import NotificationSettings from './components/account/Settings/NotificationSettings/NotificationSettings';
import SecuritySettings from './components/account/Settings/SecuritySettings/SecuritySettings';
import BrandingSettings from './components/account/Settings/BrandingSettings/BrandingSettings';
import ForgotPassword from './components/auth/Login/ForgotPassword/ForgotPassword';
import ResetPassword from './components/auth/Login/ResetPassword/ResetPassword';
import InvitePage from './components/auth/Login/InvitePage/InvitePage';
import AcceptInvitation from './components/auth/Invitations/AcceptInvitation';
import EditProperty from './components/property/EditProperty/EditProperty';
import ContractorDashboard from './components/portals/Contractor/ContractorDashboard';
import ContractorProfile from './components/portals/Contractor/ContractorProfile';
import DigitalBadge from './components/compliance/DigitalBadge/DigitalBadge';
import BadgeVerify from './components/compliance/DigitalBadge/BadgeVerify';
import ContractorTasks from './components/portals/Contractor/ContractorTasks';
import ContractorInspections from './components/portals/Contractor/ContractorInspections';
import ContractorReports from './components/portals/Contractor/ContractorReports';
import StaffDashboard from './components/portals/Staff/StaffDashboard';
import OnboardingStaff from './components/portals/Admin/OnboardingStaff';
import AccountSettings from './components/account/AccountSettings/AccountSettings';
import RoadmapPage from './components/pages/Roadmap/RoadmapPage';
import ShowcasePage from './components/pages/Showcase/ShowcasePage';
import CloudStaffDashboard from './components/portals/CloudStaff/CloudStaffDashboard';
import AssetCodeTool from './components/portals/CloudStaff/AssetCodeTool';
import AssetCodeDictionaryPage from './components/portals/CloudStaff/AssetCodeDictionaryPage';
import AssetGenerationTool from './components/portals/CloudStaff/AssetGenerationTool';
import AssetGenerationV2Page from './components/portals/CloudStaff/AssetGenerationV2Page';
import AssetGenerationPropertyPage from './components/portals/CloudStaff/AssetGenerationPropertyPage';
import UserActivityPage from './components/portals/CloudStaff/UserActivityPage';
import CloudStaffActivityLog from './components/portals/CloudStaff/CloudStaffActivityLog';
import CloudStaffBuildingLog from './components/portals/CloudStaff/CloudStaffBuildingLog';
import CloudStaffDeletedLogs from './components/portals/CloudStaff/CloudStaffDeletedLogs';
import BulkAssetEditor from './components/portals/CloudStaff/BulkAssetEditor';
import SystemHealth from './components/portals/CloudStaff/SystemHealth';
import DataIntegrity from './components/portals/CloudStaff/DataIntegrity';
import FeatureFlags from './components/portals/CloudStaff/FeatureFlags';
import SaaSMetrics from './components/portals/CloudStaff/SaaSMetrics';
import EmailTemplatesPage from './components/portals/CloudStaff/EmailTemplatesPage';
import BuildingTools from './components/portals/CloudStaff/BuildingTools';
import CompanyGeneration from './components/portals/CloudStaff/CompanyGeneration';
import CompanyHealth from './components/portals/CloudStaff/CompanyHealth';
import CloudStaffAccountCreation from './components/portals/CloudStaff/CloudStaffAccountCreation';
import AccountRecovery from './components/portals/CloudStaff/AccountRecovery';
import SystemLogs from './components/portals/CloudStaff/SystemLogs';
import CompliancePanelPage from './components/portals/CloudStaff/CompliancePanel/CompliancePanelPage';
import SupportPanelPage from './components/portals/CloudStaff/SupportPanel/SupportPanelPage';
import CrmFunnelView from './components/portals/CloudStaff/CrmFunnelView/CrmFunnelView';
import PropertyManager from './components/portals/CloudStaff/PropertyManager';
import PropertyAccessRequests from './components/portals/CloudStaff/PropertyAccessRequests';
import SupportTickets from './components/pages/Support/SupportTickets';
import SupportTicketCreate from './components/pages/Support/SupportTicketCreate';
import SupportTicketDetail from './components/pages/Support/SupportTicketDetail';
import BuildingManagerDashboard from './components/portals/BuildingManager/BuildingManagerDashboard';
import FMDashboard from './components/portals/FM/FMDashboard';
import ComplianceReport from './components/compliance/ComplianceReport/ComplianceReport';
import ContractorStaffDashboard from './components/portals/ContractorStaff/ContractorStaffDashboard';
import Toolbox from './components/portals/Contractor/Toolbox';
import AssetTagger from './components/portals/Contractor/AssetTagger';
import ContractorProperties from './components/portals/Contractor/ContractorProperties';
import NotificationsPage from './components/account/NotificationsPage/NotificationsPage';
import Reports from './components/pages/Reports/Reports';
import DrawingHotspots from './components/property/DrawingHotspots/DrawingHotspots';
import Tours from './components/property/Tours/Tours';
import ToursManagerPage from './components/portals/CloudStaff/ToursManager/ToursManagerPage';
import { USER_ROLES } from './utils/roleUtils';

// Re-flow contractor port — lazily loaded so it never bloats the main bundle.
const ContractorWorkspace = lazy(() => import('./components/portals/Contractor/workspace/ContractorWorkspace'));
// Client portal logins (a contractor's clients sign in here) — its own session, lazily loaded.
const ClientPortalApp = lazy(() => import('./components/portals/ClientPortal/ClientPortalApp'));

/** The workspace is the default contractor experience; opt out → classic (legacy) dashboard. */
const ContractorWorkspaceRoute = () => {
  if (!isContractorWorkspaceEnabled()) return <Navigate to="/contractor/legacy-dashboard" replace />;
  return (
    <Suspense fallback={<div style={{ padding: 40 }}>Loading workspace…</div>}>
      <ContractorWorkspace />
    </Suspense>
  );
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <ErrorPage />,
    future: {
      v7_skipActionErrorRevalidation: true
    },
    children: [
      {
        index: true,
        element: <Login />
      },
      {
        path: "login",
        element: <Login />
      },
      {
        path: "register",
        element: <Register />
      },
      {
        path: "coming-soon",
        element: <ComingSoon />
      },
      {
        path: "dashboard",
        element: <ProtectedRoute><Dashboard /></ProtectedRoute>
      },
      {
        path: "admin/qualifications",
        element: <ProtectedRoute allowedRoles={['admin', 'super_admin', 'cloud_staff', 'fm', 'staff']}><QualificationsAdmin /></ProtectedRoute>
      },
      {
        path: "notifications",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.STAFF, USER_ROLES.VIEWER, USER_ROLES.ADMIN, USER_ROLES.FM, USER_ROLES.SUPER_ADMIN, USER_ROLES.CONTRACTOR, USER_ROLES.BUILDING_MANAGER, USER_ROLES.CLOUD_STAFF]}>
            <NotificationsPage />
          </ProtectedRoute>
        )
      },
      {
        path: "support/tickets",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.STAFF, USER_ROLES.VIEWER, USER_ROLES.ADMIN, USER_ROLES.FM, USER_ROLES.SUPER_ADMIN, USER_ROLES.CONTRACTOR, USER_ROLES.BUILDING_MANAGER, USER_ROLES.CLOUD_STAFF]}>
            <SupportTickets />
          </ProtectedRoute>
        )
      },
      {
        path: "support/new",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.STAFF, USER_ROLES.VIEWER, USER_ROLES.ADMIN, USER_ROLES.FM, USER_ROLES.SUPER_ADMIN, USER_ROLES.CONTRACTOR, USER_ROLES.BUILDING_MANAGER, USER_ROLES.CLOUD_STAFF]}>
            <SupportTicketCreate />
          </ProtectedRoute>
        )
      },
      {
        path: "support/tickets/:id",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.STAFF, USER_ROLES.VIEWER, USER_ROLES.ADMIN, USER_ROLES.FM, USER_ROLES.SUPER_ADMIN, USER_ROLES.CONTRACTOR, USER_ROLES.BUILDING_MANAGER, USER_ROLES.CLOUD_STAFF]}>
            <SupportTicketDetail />
          </ProtectedRoute>
        )
      },
      {
        path: "reports",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.FM, USER_ROLES.CLOUD_STAFF, USER_ROLES.STAFF, USER_ROLES.VIEWER, USER_ROLES.BUILDING_MANAGER]}>
            <Reports />
          </ProtectedRoute>
        )
      },
      {
        path: "property/edit/:id",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.STAFF, USER_ROLES.ADMIN, USER_ROLES.FM, USER_ROLES.SUPER_ADMIN]}>
            <EditProperty />
          </ProtectedRoute>
        )
      },
      {
        path: "propertydashboard/:id/pinpoint/:pinpointId",
        element: <ProtectedRoute><PinpointViewer /></ProtectedRoute>
      },
      {
        path: "propertydashboard/:id",
        element: <ProtectedRoute><PropertyDashboard /></ProtectedRoute>,
        children: [
          { index: true, element: <PropertyDashboardOverview /> },
          { path: "roof-safety", element: <RoofSafety /> },
          { path: "fire-safety", element: <FireSafety /> },
          { path: "staff-safety", element: <StaffSafety /> },
          { path: "equipment-safety/*", element: <EquipmentSafety /> },
          { path: "electrical-safety/*", element: <ElectricalSafety /> },
          {
            path: "building-maintenance/*",
            element: <BuildingMaintenance />,
            children: [
              { index: true, element: <BuildingMaintenanceDashboard /> },
              { path: "dashboard", element: <BuildingMaintenanceDashboard /> },
              { path: "general", element: <GeneralMaintenance /> }
            ]
          },
          { path: "emergency-preparedness/*", element: <EmergencyPreparedness /> },
          { path: "documents", element: <Documents /> },
          { path: "drawings", element: <DrawingHotspots /> },
          { path: "tours", element: <Tours /> },
          { path: "inspections", element: <Inspections /> },
          { path: "activity", element: <ActivityPage /> },
          { path: "logs", element: <PropertyLogs /> },
          { path: "settings/*", element: <Settings /> },
        ]
      },
      {
        path: "properties/:id/reports/:reportId",
        element: <ProtectedRoute><ComplianceReport /></ProtectedRoute>
      },
      {
        path: "properties/:id",
        element: <ProtectedRoute><PropertyDashboard /></ProtectedRoute>,
        children: [
          { index: true, element: <PropertyDashboardOverview /> },
          { path: "profile", element: <ProtectedRoute allowedRoles={[USER_ROLES.STAFF, USER_ROLES.ADMIN, USER_ROLES.FM, USER_ROLES.SUPER_ADMIN]}><EditProperty /></ProtectedRoute> },
          { path: "roof-safety", element: <RoofSafety /> },
          { path: "fire-safety", element: <FireSafety /> },
          { path: "staff-safety", element: <StaffSafety /> },
          { path: "equipment-safety/*", element: <EquipmentSafety /> },
          { path: "electrical-safety/*", element: <ElectricalSafety /> },
          {
            path: "building-maintenance/*",
            element: <BuildingMaintenance />,
            children: [
              { index: true, element: <BuildingMaintenanceDashboard /> },
              { path: "dashboard", element: <BuildingMaintenanceDashboard /> },
              { path: "general", element: <GeneralMaintenance /> }
            ]
          },
          { path: "emergency-preparedness/*", element: <EmergencyPreparedness /> },
          { path: "documents", element: <Documents /> },
          { path: "drawings", element: <DrawingHotspots /> },
          { path: "tours", element: <Tours /> },
          { path: "inspections", element: <Inspections /> },
          { path: "activity", element: <ActivityPage /> },
          { path: "logs", element: <PropertyLogs /> },
          { path: "settings/*", element: <Settings /> },
        ]
      },
      {
        path: "inspections",
        element: <ProtectedRoute><Inspections /></ProtectedRoute>
      },
      {
        path: "settings/*",
        element: <ProtectedRoute><Settings /></ProtectedRoute>,
        children: [
          {
            index: true,
            element: <SettingsDashboard />
          },
          {
            path: "company",
            element: <CompanyInformation />
          },
          {
            path: "application",
            element: <ApplicationSettings />
          },
          {
            path: "notifications",
            element: <NotificationSettings />
          },
          {
            path: "security",
            element: <SecuritySettings />
          },
          {
            path: "branding",
            element: <BrandingSettings />
          }
        ]
      },
      {
        path: "forgot-password",
        element: <ForgotPassword />
      },
      {
        path: "reset-password/:token",
        element: <ResetPassword />
      },
      {
        path: "join/:token",
        element: <InvitePage />
      },
      {
        // Client portal — invited client users sign in with their own account (not a CloudSafe login).
        path: "client-portal/*",
        element: <Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}><ClientPortalApp /></Suspense>
      },
      {
        // Default contractor dashboard → the Re-flow workspace (no click-through).
        path: "contractor/dashboard",
        element: <Navigate to="/contractor/workspace" replace />
      },
      {
        // Classic dashboard kept for rollback (workspace flag 'off' lands here).
        path: "contractor/legacy-dashboard",
        element: <ProtectedRoute><ContractorDashboard /></ProtectedRoute>
      },
      {
        // Re-flow port workspace — the default. Splat so it owns its nested routing.
        path: "contractor/workspace/*",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CONTRACTOR]}>
            <ContractorWorkspaceRoute />
          </ProtectedRoute>
        )
      },
      {
        // Team invitation for an existing CloudSafe user. The page handles sign-in
        // itself (sends signed-out visitors to /login?next=…), so any role can open it.
        path: "contractor/invitations/:token",
        element: <AcceptInvitation />
      },
      {
        path: "contractor/profile",
        element: <ProtectedRoute><ContractorProfile /></ProtectedRoute>
      },
      {
        path: "contractor/id-badge",
        element: <ProtectedRoute><DigitalBadge /></ProtectedRoute>
      },
      {
        path: "contractor/qualifications",
        element: <ProtectedRoute><MyQualifications /></ProtectedRoute>
      },
      {
        path: "id-badge",
        element: <ProtectedRoute><DigitalBadge /></ProtectedRoute>
      },
      {
        path: "verify/:token",
        element: <BadgeVerify />
      },
      {
        path: "contractor/tasks",
        element: <ProtectedRoute><ContractorTasks /></ProtectedRoute>
      },
      {
        path: "contractor/inspections",
        element: <ProtectedRoute><ContractorInspections /></ProtectedRoute>
      },
      {
        path: "contractor/reports",
        element: <ProtectedRoute><ContractorReports /></ProtectedRoute>
      },
      {
        path: "contractor/properties",
        element: <ProtectedRoute><ContractorProperties /></ProtectedRoute>
      },
      {
        path: "contractor/staff-dashboard",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CONTRACTOR]}>
            <ContractorStaffDashboard />
          </ProtectedRoute>
        )
      },
      {
        path: "contractor/toolbox",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CONTRACTOR, USER_ROLES.CONTRACTOR_STAFF]}>
            <Toolbox />
          </ProtectedRoute>
        )
      },
      {
        path: "contractor/asset-tagger",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CONTRACTOR, USER_ROLES.CONTRACTOR_STAFF]}>
            <AssetTagger />
          </ProtectedRoute>
        )
      },
      {
        path: "staff/dashboard",
        element: <ProtectedRoute><StaffDashboard /></ProtectedRoute>
      },
      // Cloud Staff routes
      {
        path: "cloud-staff/dashboard",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <CloudStaffDashboard />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/asset-code-tool",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <AssetCodeTool />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/asset-code-dictionary",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <AssetCodeDictionaryPage />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/asset-generation",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <AssetGenerationTool />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/asset-generation-v2",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <AssetGenerationV2Page />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/asset-generation-v2/:id",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <AssetGenerationPropertyPage />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/asset-generation-v2/:id/:sessionId",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <OnboardingWizard />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/activity-log",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <CloudStaffActivityLog />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/building-log",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <CloudStaffBuildingLog />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/deleted-logs",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <CloudStaffDeletedLogs />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/bulk-editor",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <BulkAssetEditor />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/user-activity",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <UserActivityPage />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/system-health",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <SystemHealth />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/company-health",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <CompanyHealth />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/data-integrity",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <DataIntegrity />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/feature-flags",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <FeatureFlags />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/metrics",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <SaaSMetrics />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/email-templates",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <EmailTemplatesPage />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/building-tools",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <BuildingTools />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/company-generation",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <CompanyGeneration />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/account-creation",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <CloudStaffAccountCreation />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/account-recovery",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <AccountRecovery />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/system-logs",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN]}>
            <SystemLogs />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/tours",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <ToursManagerPage />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/compliance",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <CompliancePanelPage />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/support",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <SupportPanelPage />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/crm-funnel",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <CrmFunnelView />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/property-manager",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <PropertyManager />
          </ProtectedRoute>
        )
      },
      {
        path: "cloud-staff/property-access",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.CLOUD_STAFF, USER_ROLES.SUPER_ADMIN]}>
            <PropertyAccessRequests />
          </ProtectedRoute>
        )
      },
      // Building Manager routes
      {
        path: "building-manager/dashboard",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.BUILDING_MANAGER, USER_ROLES.STAFF, USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}>
            <BuildingManagerDashboard />
          </ProtectedRoute>
        )
      },
      // FM Dashboard routes
      {
        path: "fm/dashboard",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.STAFF, USER_ROLES.ADMIN, USER_ROLES.FM, USER_ROLES.SUPER_ADMIN]}>
            <FMDashboard />
          </ProtectedRoute>
        )
      },
      {
        path: "fm/dashboard/clients/:clientId",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.STAFF, USER_ROLES.ADMIN, USER_ROLES.FM, USER_ROLES.SUPER_ADMIN]}>
            <FMDashboard />
          </ProtectedRoute>
        )
      },
      {
        path: "fm/dashboard/:propertyId",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.STAFF, USER_ROLES.ADMIN, USER_ROLES.FM, USER_ROLES.SUPER_ADMIN]}>
            <FMDashboard />
          </ProtectedRoute>
        )
      },
      {
        path: "dashboard/onboarding",
        element: <Navigate to="/dashboard/admin-panel" replace />
      },
      {
        path: "dashboard/admin-panel",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.PROPERTY_ONBOARDING_STAFF]}>
            <OnboardingStaff />
          </ProtectedRoute>
        )
      },
      {
        path: "account-settings",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.STAFF, USER_ROLES.VIEWER, USER_ROLES.ADMIN, USER_ROLES.CONTRACTOR, USER_ROLES.SUPER_ADMIN, USER_ROLES.PROPERTY_ONBOARDING_STAFF, USER_ROLES.FM, USER_ROLES.BUILDING_MANAGER]}>
            <AccountSettings />
          </ProtectedRoute>
        )
      },
      {
        path: "roadmap",
        element: (
          <ProtectedRoute>
            <RoadmapPage />
          </ProtectedRoute>
        )
      },
      {
        path: "showcase",
        element: (
          <ProtectedRoute>
            <ShowcasePage />
          </ProtectedRoute>
        )
      },
      // Staff Safety - Coming Soon Pages
      {
        path: "properties/:id/staff-safety/training-records",
        element: <ProtectedRoute><ComingSoon featureName="Training Records" /></ProtectedRoute>
      },
      {
        path: "properties/:id/staff-safety/inductions",
        element: <ProtectedRoute><ComingSoon featureName="Inductions" /></ProtectedRoute>
      },
      {
        path: "properties/:id/staff-safety/incident-reports",
        element: <ProtectedRoute><ComingSoon featureName="Incident Reports" /></ProtectedRoute>
      },
      {
        path: "properties/:id/staff-safety/assigned-safety-tasks",
        element: <ProtectedRoute><ComingSoon featureName="Assigned Safety Tasks" /></ProtectedRoute>
      },
      {
        path: "properties/:id/staff-safety/certifications",
        element: <ProtectedRoute><ComingSoon featureName="Certifications" /></ProtectedRoute>
      },
      {
        path: "properties/:id/staff-safety/ppe-issuance-logs",
        element: <ProtectedRoute><ComingSoon featureName="PPE Issuance Logs" /></ProtectedRoute>
      },
      // Fire Safety - Coming Soon Pages
      {
        path: "properties/:id/fire-safety/fire-alarms",
        element: <ProtectedRoute><ComingSoon featureName="Fire Alarms" /></ProtectedRoute>
      },
      {
        path: "properties/:id/fire-safety/sprinklers",
        element: <ProtectedRoute><ComingSoon featureName="Sprinklers" /></ProtectedRoute>
      },
      {
        path: "properties/:id/fire-safety/emergency-lights",
        element: <ProtectedRoute><ComingSoon featureName="Emergency Lights" /></ProtectedRoute>
      },
      {
        path: "properties/:id/fire-safety/fire-doors",
        element: <ProtectedRoute><ComingSoon featureName="Fire Doors" /></ProtectedRoute>
      },
      {
        path: "properties/:id/fire-safety/fire-extinguishers",
        element: <ProtectedRoute><ComingSoon featureName="Fire Extinguishers" /></ProtectedRoute>
      },
      {
        path: "properties/:id/fire-safety/smoke-detectors",
        element: <ProtectedRoute><ComingSoon featureName="Smoke Detectors" /></ProtectedRoute>
      },
      {
        path: "properties/:id/fire-safety/heat-detectors",
        element: <ProtectedRoute><ComingSoon featureName="Heat Detectors" /></ProtectedRoute>
      },
      // Equipment Safety - Coming Soon Pages
      {
        path: "properties/:id/equipment-safety/fixed-equipment",
        element: <ProtectedRoute><ComingSoon featureName="Fixed Equipment" /></ProtectedRoute>
      },
      {
        path: "properties/:id/equipment-safety/equipment-inventory",
        element: <ProtectedRoute><ComingSoon featureName="Equipment Inventory" /></ProtectedRoute>
      },
      {
        path: "properties/:id/equipment-safety/inspection-logs",
        element: <ProtectedRoute><ComingSoon featureName="Inspection Logs" /></ProtectedRoute>
      },
      {
        path: "properties/:id/equipment-safety/maintenance-records",
        element: <ProtectedRoute><ComingSoon featureName="Maintenance Records" /></ProtectedRoute>
      },
      {
        path: "properties/:id/equipment-safety/defect-reporting",
        element: <ProtectedRoute><ComingSoon featureName="Defect Reporting" /></ProtectedRoute>
      },
      {
        path: "properties/:id/equipment-safety/documentation",
        element: <ProtectedRoute><ComingSoon featureName="Documentation" /></ProtectedRoute>
      },
      // Electrical Safety - Coming Soon Pages
      {
        path: "properties/:id/electrical-safety/circuit-breakers",
        element: <ProtectedRoute><ComingSoon featureName="Circuit Breakers" /></ProtectedRoute>
      },
      {
        path: "properties/:id/electrical-safety/equipment-logs",
        element: <ProtectedRoute><ComingSoon featureName="Equipment Logs" /></ProtectedRoute>
      },
      {
        path: "properties/:id/electrical-safety/emergency-power",
        element: <ProtectedRoute><ComingSoon featureName="Emergency Power" /></ProtectedRoute>
      },
      {
        path: "properties/:id/electrical-safety/fuse-boards",
        element: <ProtectedRoute><ComingSoon featureName="Fuse Boards" /></ProtectedRoute>
      },
      {
        path: "properties/:id/electrical-safety/grounding-bonding",
        element: <ProtectedRoute><ComingSoon featureName="Grounding & Bonding" /></ProtectedRoute>
      },
      {
        path: "properties/:id/electrical-safety/load-testing",
        element: <ProtectedRoute><ComingSoon featureName="Load Testing" /></ProtectedRoute>
      },
      {
        path: "properties/:id/electrical-safety/pat-testing",
        element: <ProtectedRoute><ComingSoon featureName="PAT Testing" /></ProtectedRoute>
      },
      {
        path: "properties/:id/electrical-safety/rcd-testing",
        element: <ProtectedRoute><ComingSoon featureName="RCD Testing" /></ProtectedRoute>
      },
      {
        path: "properties/:id/electrical-safety/switchgear",
        element: <ProtectedRoute><ComingSoon featureName="Switchgear" /></ProtectedRoute>
      },
      {
        path: "properties/:id/electrical-safety/thermal-imaging",
        element: <ProtectedRoute><ComingSoon featureName="Thermal Imaging" /></ProtectedRoute>
      },
      // Building Maintenance - Coming Soon Pages
      {
        path: "properties/:id/building-maintenance/plumbing-systems",
        element: <ProtectedRoute><ComingSoon featureName="Plumbing Systems" /></ProtectedRoute>
      },
      {
        path: "properties/:id/building-maintenance/heating-cooling",
        element: <ProtectedRoute><ComingSoon featureName="Heating & Cooling" /></ProtectedRoute>
      },
      {
        path: "properties/:id/building-maintenance/lift-maintenance",
        element: <ProtectedRoute><ComingSoon featureName="Lift Maintenance" /></ProtectedRoute>
      },
      {
        path: "properties/:id/building-maintenance/roof-maintenance",
        element: <ProtectedRoute><ComingSoon featureName="Roof Maintenance" /></ProtectedRoute>
      },
      {
        path: "properties/:id/building-maintenance/facade-maintenance",
        element: <ProtectedRoute><ComingSoon featureName="Facade Maintenance" /></ProtectedRoute>
      },
      {
        path: "properties/:id/building-maintenance/doors-windows",
        element: <ProtectedRoute><ComingSoon featureName="Doors & Windows" /></ProtectedRoute>
      },
      {
        path: "properties/:id/building-maintenance/lighting-electrical",
        element: <ProtectedRoute><ComingSoon featureName="Lighting & Electrical" /></ProtectedRoute>
      },
      {
        path: "properties/:id/building-maintenance/pest-control",
        element: <ProtectedRoute><ComingSoon featureName="Pest Control" /></ProtectedRoute>
      },
      {
        path: "properties/:id/building-maintenance/painting-repairs",
        element: <ProtectedRoute><ComingSoon featureName="Painting & Repairs" /></ProtectedRoute>
      },
      {
        path: "properties/:id/building-maintenance/defects-reporting",
        element: <ProtectedRoute><ComingSoon featureName="Defects Reporting" /></ProtectedRoute>
      },
      // Emergency Preparedness - Coming Soon Pages
      {
        path: "properties/:id/emergency-preparedness/emergency-lighting",
        element: <ProtectedRoute><ComingSoon featureName="Emergency Lighting" /></ProtectedRoute>
      },
      {
        path: "properties/:id/emergency-preparedness/escape-routes",
        element: <ProtectedRoute><ComingSoon featureName="Escape Routes" /></ProtectedRoute>
      },
      {
        path: "properties/:id/emergency-preparedness/assembly-points",
        element: <ProtectedRoute><ComingSoon featureName="Assembly Points" /></ProtectedRoute>
      },
      {
        path: "properties/:id/emergency-preparedness/emergency-plans",
        element: <ProtectedRoute><ComingSoon featureName="Emergency Plans" /></ProtectedRoute>
      },
      {
        path: "properties/:id/emergency-preparedness/fire-drills",
        element: <ProtectedRoute><ComingSoon featureName="Fire Drills" /></ProtectedRoute>
      },
      {
        path: "properties/:id/emergency-preparedness/first-aid-kits",
        element: <ProtectedRoute><ComingSoon featureName="First Aid Kits" /></ProtectedRoute>
      },
      {
        path: "properties/:id/emergency-preparedness/defibrillators",
        element: <ProtectedRoute><ComingSoon featureName="Defibrillators" /></ProtectedRoute>
      },
      {
        path: "properties/:id/emergency-preparedness/incident-reporting",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.DEMO]}>
            <ComingSoon featureName="Incident Reporting" />
          </ProtectedRoute>
        )
      }
    ]
  }
]);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
);
